import React, { useEffect, useRef, useState } from 'react';
import { 
  Ship, ScanSearch, Target, Compass, Anchor, Verified, 
  ClipboardCheck, Ruler, ClipboardList, PackageCheck, Droplets, FlaskConical 
} from 'lucide-react';
import CargoExpeditorGaugingSamplingSafetySuperintendentforLiquidCargoes from '../../assets/Cargo Expeditor  Gauging  Sampling  Safety Superintendent for Liquid Cargoes.jpg';
import CargoSurveys from '../../assets/Cargo, Hull & Machinery Damage Surveys.jpg';
import CompassAdjustments from '../../assets/Compass Adjustments..jpg';
import DraftBunkerConditionSurvey from '../../assets/DraftBunkerCondition Survey..jpg';
import ISMAudits from '../../assets/ISMISPSMLC Audits..jpg';
import PandIEntryConditionInspections from '../../assets/P&I Entry Condition Inspections.jpg';
import PortCaptaincyServices from '../../assets/Port Captaincy Services.jpg';
import PreVettingRightshipInspection from '../../assets/Pre VettingRightship Inspection..jpg';
import PreLoadingSurveys from '../../assets/Pre-Loading Surveys for Steel, Ores & Cargoes.jpg';
import SafetyNavigationalTMSAAudits from '../../assets/SafetyNavigational TMSA Audits..jpg';
import TallyConditionMeasurementSurveys from '../../assets/Tally, Condition, Measurement Surveys.jpg';
import USTInspectionforHatchWeatherTightnessandCargotankholdcleaning from '../../assets/UST Inspection for Hatch Weather Tightness and Cargo tank hold cleaning.jpg';

// --- Specialized Maritime Data with Embedded Stock Images ---
const maritimeSpecializations = [
  { 
    text: "Cargo, Hull & Machinery Damage Surveys.", 
    icon: Ship,
    image: CargoSurveys // <-- Removed curly braces here
  },
  { 
    text: "Pre-Loading Surveys for Steel, Ores & Cargoes.", 
    icon: ScanSearch,
    image: PreLoadingSurveys // <-- Removed curly braces here
  },
  { 
    text: "Tally, Condition, Measurement Surveys.", 
    icon: Target,
    image: TallyConditionMeasurementSurveys // <-- Removed curly braces here
  },
  { 
    text: "Draft/Bunker/Condition Survey.", 
    icon: Ruler,
    image: DraftBunkerConditionSurvey // <-- Removed curly braces here
  },
  { 
    text: "Port Captaincy Services.", 
    icon: Anchor,
    image: PortCaptaincyServices // <-- Removed curly braces here
  },
  { 
    text: "Compass Adjustments.", 
    icon: Compass,
    image: CompassAdjustments // <-- Removed curly braces here
  },
  { 
    text: "ISM/ISPS/MLC Audits.", 
    icon: Verified,
    image: ISMAudits // <-- Removed curly braces here
  },
  { 
    text: "Safety/Navigational / TMSA Audits.", 
    icon: ClipboardCheck,
    image: SafetyNavigationalTMSAAudits // <-- Removed curly braces here
  },
  { 
    text: "Pre Vetting/Rightship Inspection.", 
    icon: ClipboardList,
    image: PreVettingRightshipInspection // <-- Removed curly braces here
  },
  { 
    text: "P&I Entry Condition Inspections.", 
    icon: PackageCheck,
    image: PandIEntryConditionInspections // <-- Removed curly braces here
  },
  { 
    text: "UST Inspection for Hatch Weather Tightness and Cargo tank / hold cleaning.", 
    icon: Droplets,
    image: USTInspectionforHatchWeatherTightnessandCargotankholdcleaning // <-- Removed curly braces here
  },
  { 
    text: "Cargo Expeditor / Gauging / Sampling / Safety Superintendent for Liquid Cargoes.", 
    icon: FlaskConical,
    image: CargoExpeditorGaugingSamplingSafetySuperintendentforLiquidCargoes // <-- Removed curly braces here
  },
];

// --- Individual Animated Card Component ---
const AnimatedServiceCard = ({ service, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    document.title = "CMJ Global | Services";
  }, []);

  useEffect(() => {
    // 1. Store the current ref value in a variable
    const currentCardRef = cardRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { 
        threshold: 0.05, 
        rootMargin: "0px 0px -40px 0px"
      }
    );

    // 2. Use the variable to observe
    if (currentCardRef) {
      observer.observe(currentCardRef);
    }

    // 3. Use the exact same variable to cleanup and stop observing
    return () => {
      if (currentCardRef) observer.unobserve(currentCardRef);
    };
  }, []);

  const staggerDelay = (index % 3) * 100; 
  const Icon = service.icon;

  return (
    <div
      ref={cardRef}
      style={{ transitionDelay: `${staggerDelay}ms` }}
      className={`bg-white rounded-sm border border-gray-200 shadow-sm transition-all duration-700 ease-out group relative flex flex-col justify-between overflow-hidden
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}
        hover:shadow-xl hover:border-[#1E4363]/30
      `}
    >
      <div>
        {/* Top Feature Image Banner */}
        <div className="w-full h-48 overflow-hidden relative bg-gray-100">
          <img 
            src={service.image} 
            alt={service.text} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />
          {/* Dark luxury contrast overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60"></div>
          
          {/* Floating Branding Icon */}
          <div className="absolute bottom-4 left-6 w-12 h-12 bg-[#F9F8F3] text-[#1E4363] rounded-sm flex items-center justify-center shadow-md group-hover:bg-[#1E4363] group-hover:text-white transition-colors duration-300">
            <Icon size={24} strokeWidth={1.5} />
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6">
          <p className="text-lg font-bold text-[#2C3539] leading-tight mb-4 font-serif group-hover:text-[#1E4363] transition-colors">
            {service.text}
          </p>
        </div>
      </div>
      
      {/* Structural Card Footer decoration */}
      <div className="px-6 pb-6 mt-auto">
        <Compass className="text-[#1E4363]/5 ml-auto transform group-hover:rotate-45 transition-transform duration-700" size={36} />
      </div>
    </div>
  );
};

// --- Main Services Page Component ---
export default function Services() {
  return (
    <section className="py-24 bg-[#F9F8F3] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold text-[#87412A] uppercase tracking-widest block mb-2">
            Our Expertise
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1E4363] font-serif mb-6 uppercase tracking-tight">
            Our Maritime Specializations
          </h2>
          <div className="w-20 h-1 bg-[#87412A] mx-auto mb-8"></div>
        </div>

        {/* Responsive Flex/Grid Workspace */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {maritimeSpecializations.map((service, index) => (
            <AnimatedServiceCard key={index} service={service} index={index} />
          ))}
        </div>

        {/* Tagline Footer */}
        <div className="mt-24 pt-12 border-t border-gray-200 text-center">
          <p className="text-2xl md:text-3xl font-bold text-[#87412A] font-serif tracking-wide">
            "Precision Surveys. Risk Controlled. Compliance Assured."
          </p>
        </div>

      </div>
    </section>
  );
}